import GlobalStyle from '../../utils/sass/global.module.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FiHeadphones, FiRefreshCw, FiSearch } from 'react-icons/fi';
import { fetchAdminUserChats, getFullChatAudit } from '../../store/chat/chatActions';
import { getUsersList } from '../../store/user/userActions';
import { getImage } from '../../utils';

function chatListFileLabel(message) {
	if (!message?.isFile) return null;
	const raw =
		message.file?.originalName ||
		message.file?.originalname ||
		message.file?.name;
	if (typeof raw === 'string' && raw.trim()) return raw.trim();
	return 'File';
}

function chatListPreviewText(message) {
	if (!message) return '';
	if (message.hidden === true) return 'Deleted message';
	if (message.isFile) return chatListFileLabel(message) ?? 'File';
	return message.message ?? '';
}

function transcriptLine(message) {
	if (message?.hidden === true) {
		return '(deleted)';
	}
	if (message?.isFile) {
		return chatListFileLabel(message) ?? 'File';
	}
	return message?.message ?? '';
}

const UserConversationsAudit = () => {
	const dispatch = useDispatch();
	const [users, setUsers] = useState([]);
	const [userSearch, setUserSearch] = useState('');
	const [selectedUser, setSelectedUser] = useState(null);
	const [chats, setChats] = useState([]);
	const [chatsLoading, setChatsLoading] = useState(false);
	const [chatsError, setChatsError] = useState(null);
	const [selectedChatId, setSelectedChatId] = useState(null);
	const [transcript, setTranscript] = useState(null);
	const [transcriptLoading, setTranscriptLoading] = useState(false);

	useEffect(() => {
		dispatch(getUsersList())
			.unwrap()
			.then((list) => setUsers(Array.isArray(list) ? list : []))
			.catch(() => setUsers([]));
	}, [dispatch]);

	const filteredUsers = useMemo(() => {
		const q = userSearch.trim().toLowerCase();
		if (!q) return users.slice(0, 20);
		return users
			.filter(
				(u) =>
					String(u.firstName ?? '')
						.toLowerCase()
						.includes(q) ||
					String(u.lastName ?? '')
						.toLowerCase()
						.includes(q) ||
					String(u.email ?? '')
						.toLowerCase()
						.includes(q),
			)
			.slice(0, 30);
	}, [users, userSearch]);

	const loadChatsForUser = useCallback(
		async (user) => {
			if (!user?._id) return;
			setChatsLoading(true);
			setChatsError(null);
			setChats([]);
			setSelectedChatId(null);
			setTranscript(null);
			try {
				const rows = await dispatch(fetchAdminUserChats(user._id)).unwrap();
				setChats(Array.isArray(rows) ? rows : []);
			} catch (e) {
				setChatsError(e?.error ?? e?.message ?? 'Failed to load chats');
			} finally {
				setChatsLoading(false);
			}
		},
		[dispatch],
	);

	const onSelectUser = (user) => {
		setSelectedUser(user);
		loadChatsForUser(user);
	};

	const onSelectChat = async (chat) => {
		const id = chat?._id;
		if (!id) return;
		setSelectedChatId(id);
		setTranscriptLoading(true);
		setTranscript(null);
		try {
			const data = await dispatch(
				getFullChatAudit({
					id,
					perspectiveProfileId: chat.viewerProfileId,
				}),
			).unwrap();
			setTranscript(data);
		} catch {
			setTranscript(null);
		} finally {
			setTranscriptLoading(false);
		}
	};

	const onRefreshChats = () => {
		if (selectedUser) loadChatsForUser(selectedUser);
	};

	return (
		<div>
			<h1 className={GlobalStyle.pageTitle}>User conversations (audit)</h1>
			<p className="text-sm text-gray-600 mb-4 max-w-3xl">
				Select a user to list every chat their profiles participate in. Transcripts are read-only
				and do not mark messages as read. Deleted messages appear in the list and in history.
			</p>

			<div className="flex flex-col lg:flex-row gap-4 mb-4">
				<div className="relative flex-1 max-w-md">
					<FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
					<input
						type="search"
						className="w-full border rounded-lg pl-10 pr-3 py-2 text-sm"
						placeholder="Search users by name or email…"
						value={userSearch}
						onChange={(e) => setUserSearch(e.target.value)}
					/>
				</div>
				<button
					type="button"
					className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm disabled:opacity-50"
					onClick={onRefreshChats}
					disabled={!selectedUser || chatsLoading}
				>
					<FiRefreshCw size={16} className={chatsLoading ? 'animate-spin' : ''} />
					Refresh chats
				</button>
			</div>

			<div className="flex flex-wrap gap-5 lg:flex-nowrap">
				<div className="flex min-h-[400px] h-[800px] max-md:max-h-screen w-full max-w-[360px] shrink-0 flex-col bg-white p-5 rounded-[20px]">
					<div className="text-xs font-semibold text-gray-500 uppercase mb-2">Users</div>
					<div className="overflow-y-auto flex-1 border rounded-xl max-h-[320px] mb-4">
						{filteredUsers.length === 0 ? (
							<div className="p-4 text-sm text-gray-500">No users match.</div>
						) : (
							filteredUsers.map((u) => (
								<button
									type="button"
									key={u._id}
									className={`w-full text-left px-3 py-2 text-sm border-b last:border-b-0 hover:bg-gray-50 ${
										selectedUser?._id === u._id ? 'bg-nt-blue/10' : ''
									}`}
									onClick={() => onSelectUser(u)}
								>
									<div className="font-medium capitalize">
										{u.firstName} {u.lastName}
									</div>
									<div className="text-xs text-gray-500 truncate">{u.email}</div>
								</button>
							))
						)}
					</div>

					<div className="text-xs font-semibold text-gray-500 uppercase mb-2">Conversations</div>
					{chatsError ? (
						<div className="text-sm text-red-600 mb-2">{chatsError}</div>
					) : null}
					{chatsLoading ? (
						<div className="text-sm text-gray-500">Loading…</div>
					) : !selectedUser ? (
						<div className="text-sm text-gray-500">Pick a user first.</div>
					) : chats.length === 0 ? (
						<div className="text-sm text-gray-500">No chats for this user.</div>
					) : (
						<div className="flex max-h-[400px] flex-1 flex-col gap-2.5 overflow-y-auto">
							{chats.map((chat) => {
								const selected = selectedChatId === chat._id;
								const preview = chatListPreviewText(chat.message);
								const deletedLast = Boolean(chat.message?.hidden);
								return (
									<button
										type="button"
										key={chat._id}
										className={`group relative flex w-full cursor-pointer items-center gap-2.5 rounded-[5px] border-b border-nt-grey-light p-2.5 text-left last:border-b-0 hover:bg-nt-grey-light ${
											selected ? 'bg-nt-grey-light' : ''
										}`}
										onClick={() => onSelectChat(chat)}
									>
										{chat.support === true ? (
											<span
												className={`flex h-[50px] w-[50px] shrink-0 items-center justify-center rounded-full ${
													selected ? 'bg-white' : 'bg-nt-grey-light'
												} text-nt-blue`}
											>
												<FiHeadphones size={28} />
											</span>
										) : (
											<img
												className={`h-[50px] w-[50px] shrink-0 rounded-full border-2 object-cover ${
													selected
														? 'border-white'
														: 'border-nt-grey-light group-hover:border-white'
												}`}
												src={getImage(chat.user?.avatar, 'AVATAR')}
												alt=""
											/>
										)}
										<div className="min-w-0 flex-1 text-sm">
											<div className="capitalize">
												{chat.support === true
													? 'Support'
													: `${chat.user?.firstName ?? ''} ${chat.user?.lastName ?? ''}`.trim() ||
													  'Unknown'}
											</div>
											{chat.message ? (
												<div
													className={`truncate font-light ${
														deletedLast ? 'italic text-gray-500 line-through' : ''
													}`}
													title={preview}
												>
													{preview}
												</div>
											) : null}
										</div>
									</button>
								);
							})}
						</div>
					)}
				</div>

				<div className="flex min-h-[400px] h-[800px] max-md:max-h-screen flex-1 flex-col bg-white p-5 rounded-[20px] min-w-0">
					<div className="text-xs font-semibold text-gray-500 uppercase mb-2">Transcript (read-only)</div>
					{transcriptLoading ? (
						<div className="text-sm text-gray-500">Loading messages…</div>
					) : !selectedChatId ? (
						<div className="text-sm text-gray-500">Select a conversation.</div>
					) : transcript?.messages?.length ? (
						<ul className="w-full flex flex-col gap-2 overflow-y-auto flex-1 pr-2">
							{transcript.messages.map((m) => (
								<li
									key={m._id}
									className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
										m.hidden
											? 'bg-gray-100 text-gray-500 border border-dashed'
											: m.isMine
												? 'bg-nt-blue text-white self-end ml-auto'
												: 'bg-gray-100 self-start'
									}`}
								>
									{m.hidden ? (
										<span className="italic">Deleted message</span>
									) : (
										transcriptLine(m)
									)}
								</li>
							))}
						</ul>
					) : (
						<div className="text-sm text-gray-500">No messages.</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default UserConversationsAudit;
